/**
 * target-store — R232q20 Phase 1 Foundation.
 *
 * Pure client-side store for the player's current target / target-of-target /
 * mouseover unit. WoW Sirus 3.3.5a parity:
 *   – `targetId`   the unit explicitly clicked / Tab-targeted; survives until
 *                  the player clicks elsewhere or the unit despawns.
 *   – `totId`      target-of-target — the unit that `targetId` is currently
 *                  targeting. Drives the small ToT frame next to the main
 *                  target frame. Now derived from net-store: when the
 *                  targeted player has their own `targetId` populated,
 *                  consumers read `useNet().players[targetId].targetId`.
 *   – `mouseoverId` unit currently under the cursor (nameplate hover, world
 *                   click-target preview). Cleared when cursor leaves.
 *
 * R232q20-Sirus-wire: `setTarget` now dispatches a Colyseus `setTarget`
 * message after the optimistic local update so the server can replicate
 * `player.targetId` to every observer (party assists, focus frames).
 *
 * NOT persisted — target context is ephemeral and must reset on reload.
 *
 * NOTE: this file only owns the *state*. Resolving an entityId → unit row
 * (HP, level, classification…) is the consumer's job; they pull the row
 * from `net-store.ts` (`useNet().players[id]` or `.mobs[id]`).
 */

import { create } from 'zustand';
import { sendSetTarget } from '../game/realmClient.js';
import { useNet } from './net-store.js';

export interface TargetStore {
  /** Active target's entityId (player or mob), or null. */
  targetId: string | null;
  /** Target-of-target. Kept for back-compat callers; new code should
   *  prefer `useTotId()` which reads from net-store directly. */
  totId: string | null;
  /** Unit currently under the cursor / nameplate-hovered. */
  mouseoverId: string | null;

  /** Set or clear the active target. Optimistically updates local state and
   *  dispatches a `setTarget` message to the server in the same call. */
  setTarget: (id: string | null) => void;
  /** Set or clear the mouseover entity. */
  setMouseover: (id: string | null) => void;
  /** Clear all three slots — used on death, zone change, logout. */
  clear: () => void;
}

export const useTarget = create<TargetStore>((set) => ({
  targetId: null,
  totId: null,
  mouseoverId: null,

  setTarget: (id) => {
    // 1) Optimistic local update — UI reflects the click instantly even on
    //    high-latency connections. ToT becomes stale; the net-store
    //    subscriber refills it once the next state delta arrives (or
    //    consumers can read `useTotId()` for an always-fresh value).
    set({ targetId: id, totId: null });
    // 2) Server dispatch — replicates the relationship so every observer
    //    sees who this player is focused on (party assists, target-of-
    //    target lookups for nearby allies).
    try {
      sendSetTarget(id);
    } catch {
      // Connection not yet established — the local-only fallback above
      // already handled the UX path; the server will catch up on the
      // next setTarget call.
    }
  },

  setMouseover: (id) => {
    set({ mouseoverId: id });
  },

  clear: () => {
    set({ targetId: null, totId: null, mouseoverId: null });
    try { sendSetTarget(null); } catch { /* see setTarget */ }
  },
}));

/**
 * R232q20-Sirus-wire: derived target-of-target id. Reads the current
 * targetId from useTarget, looks up the targeted player's `targetId`
 * field in the net-store, and returns it. Returns `null` for mob
 * targets (mobs don't replicate a `targetId` field — they use `target`
 * which is already mirrored on `MobView`).
 *
 * Usage:
 *   const totId = useTotId();
 *   const tot = useNet((s) => totId ? s.players[totId] ?? s.mobs[totId] : null);
 */
export function useTotId(): string | null {
  const targetId = useTarget((s) => s.targetId);
  const players = useNet((s) => s.players);
  const mobs = useNet((s) => s.mobs);
  if (!targetId) return null;
  const tp = players[targetId];
  if (tp && typeof tp.targetId === 'string' && tp.targetId.length > 0) return tp.targetId;
  const tm = mobs[targetId];
  if (tm && typeof tm.target === 'string' && tm.target.length > 0) return tm.target;
  return null;
}
